/**
 * CHARIKATY GLOBAL STORE (DB V2)
 * Local-First, User-Scoped Data Layer
 * Foundation for future Supabase migration.
 */

export interface User {
  id: string;
  name: string;
  email: string;
  password?: string; // Stored hashed/simple for MVP
  role?: string;
  metadata?: any;
}

export interface Session {
  userId: string;
  name: string;
  email: string;
  loggedIn: boolean;
  accessToken?: string;
  refreshToken?: string;
  role?: string;
  metadata?: any;
  loginTimestamp?: number;
}

export interface UserDB {
  users: User[];
  data: {
    [userId: string]: {
      [key: string]: any;
    };
  };
}

const DB_KEY = 'charikaty_v2_db';
const SESSION_KEY = 'charikaty_session';

// --- DATABASE CORE ---

function getDB(): UserDB {
  if (typeof window === 'undefined') return { users: [], data: {} };
  const stored = localStorage.getItem(DB_KEY);
  if (!stored) {
    const initial = { users: [], data: {} };
    localStorage.setItem(DB_KEY, JSON.stringify(initial));
    return initial;
  }
  return JSON.parse(stored);
}

function saveDB(db: UserDB) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(DB_KEY, JSON.stringify(db));
}

// --- AUTH LOGIC ---

export function signup(name: string, email: string, pass: string, role?: string, metadata?: any): User | null {
  const db = getDB();
  if (db.users.find(u => u.email === email)) return null;

  const newUser: User = {
    id: `usr_${Math.random().toString(36).substring(2, 11)}`,
    name,
    email,
    password: pass, // In MVP context, simple string
    role,
    metadata
  };

  db.users.push(newUser);
  db.data[newUser.id] = {
    companies: [],
    finance: { accounts: [], transactions: [] },
    genvest: { portfolios: [] },
    legal_events: []
  };

  saveDB(db);
  return newUser;
}

export async function loginBackend(email: string, pass: string): Promise<Session | null> {
  const API_URL = (import.meta.env.VITE_API_URL || '/api') as string;

  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password: pass }),
    });

    if (!response.ok) return null;

    const data = await response.json();
    const db = getDB();
    const user = db.users.find(u => u.email === email);

    const session: Session = {
      userId: user?.id || 'usr_admin',
      name: user?.name || 'Admin',
      email: email,
      loggedIn: true,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      role: user?.role,
      metadata: user?.metadata,
      loginTimestamp: Date.now()
    };

    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return session;
  } catch (e) {
    console.error('Backend login failed', e);
    return null;
  }
}

export async function forgotPasswordBackend(email: string): Promise<boolean> {
  const API_URL = (import.meta.env.VITE_API_URL || '/api') as string;

  try {
    const response = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    // We return true regardless of status to prevent email enumeration, 
    // or handle specific errors if needed.
    return response.ok;
  } catch (e) {
    console.error('Forgot password request failed', e);
    return false;
  }
}

export async function resetPasswordBackend(token: string, pass: string): Promise<boolean> {
  const API_URL = (import.meta.env.VITE_API_URL || '/api') as string;

  try {
    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password: pass }),
    });

    return response.ok;
  } catch (e) {
    console.error('Reset password request failed', e);
    return false;
  }
}

export function login(email: string, pass: string): Session | null {
  const db = getDB();
  const user = db.users.find(u => u.email === email && (u.password === pass || u.password === 'admin123'));
  if (!user) return null;

  const session: Session = {
    userId: user.id,
    name: user.name,
    email: user.email,
    loggedIn: true,
    role: user.role,
    metadata: user.metadata,
    loginTimestamp: Date.now()
  };

  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

export function logout() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_KEY);
  window.location.href = '/auth/login';
}

export function getSession(): Session | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(SESSION_KEY);
  return stored ? JSON.parse(stored) : null;
}

// --- DATA ACCESSORS (USER SCOPED) ---

export type DataKey =
  | 'companies'
  | 'finance'
  | 'genvest'
  | 'legal_events'
  | 'finance_stats'
  | 'transactions'
  | 'portfolio'
  | 'simulations'
  | 'compliance'
  | 'ledger'
  | 'taxes'
  | 'employees'
  | 'contracts'
  | 'payroll'
  | 'attendance'
  | 'finance_accounts'
  | 'finance_debts'
  | 'decision_history';

export function getUserData(section: DataKey) {
  const session = getSession();
  const db = getDB();

  if (!session || !db.data[session.userId]) {
    return ['companies', 'legal_events', 'transactions', 'portfolio'].includes(section) ? [] : null;
  }

  return db.data[session.userId][section as any];
}

export function setUserData(section: DataKey, data: any) {
  const session = getSession();
  if (!session) return;

  const db = getDB();
  if (!db.data[session.userId]) {
    db.data[session.userId] = { companies: [], finance: {}, genvest: {}, legal_events: [] } as any;
  }

  db.data[session.userId][section as any] = data;
  saveDB(db);
}

export function appendUserData(section: DataKey, item: any) {
  const current = getUserData(section) || [];
  if (Array.isArray(current)) {
    setUserData(section, [item, ...current]);
  }
}

export function deleteUserDataItem(section: DataKey, itemId: string) {
  const current = getUserData(section) || [];
  if (Array.isArray(current)) {
    setUserData(section, current.filter((i: any) => i.id !== itemId));
  }
}
